<template>
	<section class="insicht insicht--fade-in" v-htmlclass="customHtmlClasses">
		<div class="vouchers__container">
			<div class="vouchers__content">
				<Button
					v-if="content.videoUrl"
					type="button"
					level="tertiary"
					:text="$t('vouchers.tutorialButton')"
					arrow
					@click.prevent="onVideoClick(content.videoUrl)"
				/>
			</div>
			<GeneralMessage
				ref="initialError"
				v-if="initialError"
				:message="initialError"
				type="error"
			/>
		</div>
		<div class="vouchers__container">
			<ul class="vouchers__list">
				<li
					class="vouchers__item"
					v-for="(voucher, index) in vouchers"
					:key="index"
				>
					<img
						class="vouchers__item-image"
						:src="voucher.image"
						alt="Voucher Image"
					>
					<span
						class="vouchers__item-title"
						v-html="voucher.title"
					/>
					<p
						class="vouchers__item-text"
						v-html="voucher.text"
					/>
					<div class="vouchers__item-price">
						<span
							class="vouchers__item-price-value"
							v-if="!voucher.custom"
							v-html="`${$options.filters.madpricecent(voucher.price * 100)} / ${$t('vouchers.piece')}`"
						/>
						<template v-else>
							<span
								class="vouchers__item-price-prefix"
								v-html="themeValue('THEME_CURRENCY_PREFIX')"
							/>
							<input
								v-model="voucher.price"
								type="number"
								min="0"
								step="1"
								class="vouchers__item-price-input"
								@input="onInput(voucher)"
							>
							<span
								class="vouchers__item-price-suffix"
								v-html="`/ ${$t('vouchers.piece')}`"
							/>
						</template>
					</div>
					<QuantityCounter
						:quantity="voucher.quantity"
						base-class="vouchers__item-quantity"
						@sub="onSub(voucher)"
						@add="onAdd(voucher)"
					/>
				</li>
			</ul>
		</div>

		<div
			class="vouchers__sum"
			v-if="showSum"
		>
			<div
				class="vouchers__sum-item"
				v-for="(voucher, index) in activeVouchers"
				:key="index"
			>
				<span
					class="vouchers__sum-item-title"
					v-html="voucher.title"
				/>
				<span
					class="vouchers__sum-item-quantity"
					v-html="voucher.quantity"
				/>
				<span
					class="vouchers__sum-item-price"
					v-html="$options.filters.madpricecent(voucher.quantity * (voucher.price * 100))"
				/>
			</div>
			<div class="vouchers__sum-total">
				<span
					class="vouchers__sum-total-title"
					v-html="$t('vouchers.total')"
				/>
				<span
					class="vouchers__sum-total-price"
					v-html="$options.filters.madpricecent(total)"
				/>
			</div>
		</div>

		<div
			class="vouchers__sum"
			v-else
		>
			<div
				class="vouchers__sum-item"
			>
				<span
					class="vouchers__sum-item-title"
					v-html="$t('vouchers.noVouchersSelected')"
				/>
			</div>
		</div>

		<div
			class="vouchers__sticky"
			v-if="showSum"
		>
			<div class="vouchers__sticky-content">
				<div class="vouchers__sticky-count">
					<div
						class="vouchers__sticky-count-label"
						v-html="$t('vouchers.vouchers')"
					/>
					<div
						class="vouchers__sticky-count-value"
						v-html="totalCount"
					/>
				</div>

				<div class="vouchers__sticky-total">
					<div
						class="vouchers__sticky-total-label"
						v-html="$t('vouchers.total')"
					/>
					<div
						class="vouchers__sticky-total-value"
						v-html="$options.filters.madpricecent(total)"
					/>
				</div>
			</div>
			<Button
					type="button"
					level="primary"
					:text="$t('vouchers.orderButton')"
					@click="scrollToForm"
				/>
		</div>

		<div
			class="vouchers__form"
			v-if="showSum"
		>
			<GeneralMessage
				ref="message"
				v-if="response.message"
				:message="response.message"
				:type="response.type"
				stick
			/>

			<ValidationObserver
				ref="form"
				v-slot="{ invalid, errors }"
				tag="form"
				class="vouchers__form-wrapper"
				novalidate
				@submit.prevent="onSubmit"
			>

				<h2
					class="vouchers__form-title"
					v-html="$t('vouchers.contactTitle')"
				/>

				<div class="form-row">
					<ValidationProvider
						class="form-row__wrapper"
						name="email"
						rules="required|email"
						v-slot="{ errors, invalid, validated }"
						tag="div"
						:customMessages="{ required: $t('forms.errors.fieldEmpty'), email: $t('forms.errors.emailInvalid') }"
					>
						<FormInput
							type="email"
							:placeholder="$t('forms.labels.email')"
							:label="$t('forms.labels.email')"
							name="email"
							v-model="form.email"
							:error="errors.length > 0"
							:error-message="errors[0]"
							:hint="$t('vouchers.emailHint')"
							required
						/>
					</ValidationProvider>
					<ValidationProvider
						class="form-row__wrapper"
						name="shippingPhone"
						rules="phone_int"
						v-slot="{ errors, invalid, validated }"
						tag="div"
						:customMessages="{ phone_int: $t('forms.errors.phone') }"
					>
						<FormInput
							type="tel"
							:placeholder="$t('forms.labels.phone')"
							:label="$t('forms.labels.phone')"
							:labelOption="$t('forms.labels.optional')"
							name="phone"
							v-model="form.phoneNumber"
							:error="errors.length > 0"
							:error-message="errors[0]"
							:hint="$t('vouchers.phoneHint')"
						/>
					</ValidationProvider>
				</div>

				<h2
					class="vouchers__form-title"
					v-html="$t('vouchers.addressTitle')"
				/>

				<ValidationObserver
					tag="section"
					ref="shippingAddress"
					vid="shippingAddress"
					v-slot="{ invalid }"
				>

					<div class="form-row">
						<ValidationProvider
							class="form-row__wrapper"
							name="shippingFirstname"
							rules="required"
							v-slot="{ errors, invalid, validated }"
							tag="div"
							:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
						>
							<FormInput
								type="text"
								:placeholder="$t('forms.labels.firstname')"
								:label="$t('forms.labels.firstname')"
								name="firstname"
								v-model="form.firstname"
								:error="errors.length > 0"
								:error-message="errors[0]"
								required
							/>
						</ValidationProvider>
						<ValidationProvider
							class="form-row__wrapper"
							name="shippingLastname"
							rules="required"
							v-slot="{ errors, invalid, validated }"
							tag="div"
							:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
						>
							<FormInput
								type="text"
								:placeholder="$t('forms.labels.lastname')"
								:label="$t('forms.labels.lastname')"
								name="lastname"
								v-model="form.lastname"
								:error="errors.length > 0"
								:error-message="errors[0]"
								required
							/>
						</ValidationProvider>
					</div>

					<div class="form-row">
						<FormInput
							type="text"
							:placeholder="$t('forms.labels.company')"
							:label="$t('forms.labels.company')"
							:labelOption="$t('forms.labels.optional')"
							name="company"
							v-model="form.company"
							:required="false"
						/>
					</div>

					<div class="form-row">
						<ValidationProvider
							class="form-row__wrapper"
							name="shippingStreet"
							rules="required"
							v-slot="{ errors, invalid, validated }"
							tag="div"
							:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
						>
							<FormInput
								type="text"
								:placeholder="$t('forms.labels.streetNumber')"
								:label="$t('forms.labels.streetNumber')"
								name="street"
								v-model="form.street"
								:error="errors.length > 0"
								:error-message="errors[0]"
								required
							/>
						</ValidationProvider>

						<ValidationObserver
							class="form-row__wrapper"
							tag="div"
							ref="shippingZipCity"
							v-slot="{ dirty, validated, invalid, errors }"
						>
							<div class="form-group">
								<ValidationProvider
									class="form-group__span--4 form-group__span--xxl-3"
									name="shippingZip"
									rules="required|zip"
									v-slot="{ errors, invalid }"
									tag="div"
									:customMessages="{ required: $t('forms.errors.fieldEmpty'), zip: $t('forms.errors.zip') }"
								>
									<FormInputZip
										:placeholder="$t('forms.labels.zip')"
										:label="$t('forms.labels.zip')"
										name="zip"
										v-model="form.postcode"
										:error="errors.length > 0"
										required
									/>
								</ValidationProvider>
								<ValidationProvider
										class="form-group__span--8 form-group__span--xxl-9"
										name="shippingCity"
										rules="required"
										v-slot="{ errors, invalid }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
									>
									<FormInput
										type="text"
										:placeholder="$t('forms.labels.city')"
										:label="$t('forms.labels.city')"
										name="city"
										v-model="form.city"
										:error="errors.length > 0"
										required
									/>
								</ValidationProvider>
							</div>
							<div
								class="form-input__error | form-input-error form-group__span--12"
								v-if="invalid && (validated || dirty)"
								v-html="joinErrorMessage(errors)"
							/>
						</ValidationObserver>
					</div>
					<div class="form-row">
						<div class="form-row__wrapper">
							<FormInput
								type="text"
								:placeholder="$t('forms.labels.addressDetails')"
								:label="$t('forms.labels.addressDetails')"
								:labelOption="$t('forms.labels.optional')"
								name="addressdetails"
								v-model="form.details"
								:required="false"
								:hint="$t('forms.labels.courierHint')"
								:disabled="formLocked"
							/>
						</div>
						<ValidationProvider
							class="form-row__wrapper"
							ref="shippingCountry"
							name="shippingCountry"
							rules="required"
							v-slot="{ errors, invalid }"
							tag="div"
							:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
						>
							<FormInputSelect
								:options="countryOptions"
								v-model="form.countryCode"
								:label="$t('forms.labels.country')"
								name="country"
								:disabled="formLocked"
							/>
						</ValidationProvider>
					</div>
				</ValidationObserver>

				<div class="form-row">
					<FormInputTextarea
						:placeholder="$t('forms.labels.notes')"
						:label="$t('forms.labels.notes')"
						v-model="form.message"
					/>
					<!-- <FormInputTextarea
						v-if="holidayVouchers"
						:placeholder="$t('vouchers.message')"
						:label="$t('vouchers.message')"
						:hint="$t('vouchers.messageLength', { length: 100 })"
						:maxlength="100"
						v-model="form.giftMessage"
					/> -->
				</div>


				<ValidationProvider
					:rules="{ required: { allowFalse: false } }"
					name="checkAgb"
					tag="div"
					v-slot="{ invalid, errors, validated }"
					:customMessages="{ required: $t('vouchers.errorTerms') }"
				>
					<FormInputCheckbox
						name="terms"
						value="checkAgb"
						v-model="form.checkAgb"
						:error="errors.length > 0"
						:error-message="errors[0]"
						immediate
					>
						<span v-html="$t('vouchers.termsLabel')"/>
					</FormInputCheckbox>
				</ValidationProvider>

				<Button
					type="submit"
					level="primary"
					:text="$t('vouchers.orderButton')"
					:disabled="formLocked"
				/>
			</ValidationObserver>
		</div>

		<Modal
			ref="videoModal"
		>
			<div
				class="overlay-video"
				v-html="videoEmbedCode"
			/>
		</Modal>
	</section>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate"
import { mapGetters } from "vuex"
import scrrroll from 'scrrroll'
import FormInput from "@/components/FormInput"
import FormInputZip from "@/components/FormInputZip"
import FormInputTextarea from "@/components/FormInputTextarea"
import FormInputCheckbox from "@/components/FormInputCheckbox"
import FormInputSelect from "@/components/FormInputSelect"
import GenericMixin from '@/mixins/GenericVueMixin'
import QuantityCounter from '@/components/QuantityCounter';
import Button from '@/components/Button'
import GeneralMessage from '@/components/GeneralMessage'
import Modal from '@/components/Modal'
import { format } from '@/utils/format'
import videoEmbed from '@/utils/videoEmbed'
import {contaoService, datatrans} from '@/service';

export default {
	mixins: [
		GenericMixin,
	],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputZip,
		FormInputTextarea,
		FormInputCheckbox,
		FormInputSelect,
		Button,
		GeneralMessage,
		Modal,
		QuantityCounter
	},
	data() {
		return {
			countryOptions: (this.themeValue('THEME_COUNTRY_LIST') || []).map(country => ({
				value: country,
				label: this.$t(`forms.countries.${country}`)
			})),
			initialError: this.$root.options.initialError,
			content: this.$root.options.content,
			config: this.$root.options.config,
			vouchers: this.$root.options.vouchers.map(voucher => {
				const price = parseFloat(voucher.price);

				return {
					...voucher,
					custom: isNaN(price),
					price: isNaN(price) ? 0 : price,
					quantity: 0,
				};
			}),
			holidayVouchers: this.$root.options.holidayVouchers,
			form: {
				firstname: '',
				lastname: '',
				company: '',
				street: '',
				postcode: '',
				city: '',
				details: '',
				countryCode: this.themeValue('THEME_COUNTRY'),
				phoneNumber: '',
				email: '',
				message: '',
				giftMessage: '',
				checkAgb: false,
				asGift: false
			},
			response: {
				type: 'info',
				message: '',
			},
			formLocked: false,
			videoEmbedCode: '',
			customHtmlClasses: {
				'hide--chat': true
			},
		}
	},
	computed: {
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		activeVouchers() {
			return this.vouchers.filter(voucher => voucher.quantity);
		},
		showSum() {
			return this.vouchers.reduce((p, voucher) => {
				return p || !!voucher.quantity;
			}, false);
		},
		totalCount() {
			return this.vouchers.reduce((p, voucher) => {
				return p + voucher.quantity;
			}, 0);
		},
		total() {
			return this.vouchers.reduce((p, voucher) => {
				return p + (voucher.quantity * (voucher.price * 100));
			}, 0);
		}
	},
	methods: {
		joinErrorMessage(errors) {
			let messages = [];
			Object.values(errors).filter(value => !!value.join('').trim()).forEach(m => {
				const message = m.join('').trim();
				if (!messages.includes(message)) {
					messages.push(message);
				}
			});
			return messages.join(' ');
		},
		onSub(voucher) {
			if (voucher.price > 0) {
				voucher.quantity = Math.max(voucher.quantity - 1, 0);
			}
			else {
				voucher.quantity = 0;
			}
		},
		onAdd(voucher) {
			if (voucher.price > 0) {
				voucher.quantity = voucher.quantity + 1;
			}
			else {
				voucher.quantity = 0;
			}
		},
		onInput(voucher) {
			if (voucher.price > 0) {
				voucher.quantity = voucher.quantity || 1;
			}
			else {
				voucher.quantity = 0;
			}
		},
		onVideoClick(url) {
			this.videoEmbedCode = videoEmbed(url);
			this.$refs.videoModal.open();
		},
		scrollToForm() {
			if (this.$refs.form) {
				scrrroll.to(this.$refs.form.$el);
			}
		},
		async onSubmit(e) {
			e.preventDefault();

			const valid = await this.$refs.form.validate();

			if (valid) {
				this.loading = true;
				try {
					const response = await contaoService.orderVouchers({
						...this.form,
						holidayVouchers: this.holidayVouchers,
						vouchers: [...this.activeVouchers],
						lang: this.language,
						contentId: this.config?.contentId || null
					});

					if (response.status) {
						this.response.message = '';
						this.loading = false;

						let result = await datatrans.payment({
							currency: this.themeValue('THEME_CURRENCY'),
							amount: this.total,
							ref: response.ref,
							merchantId: this.config.merchantId,
							sign: response.sign,
							successUrl: format(this.config.successUrl, {ref: response.ref}),
							cancelUrl: format(this.config.cancelUrl, {ref: response.ref}),
							errorUrl: format(this.config.errorUrl, {ref: response.ref}),
							additionalData: {
								action: 'buy',
								language: this.language,
								uppCustomerDetails: "yes",
								uppCustomerTitle: "",
								uppCustomerFirstName: this.form.firstname,
								uppCustomerLastName: this.form.lastname,
								uppCustomerStreet: this.form.street,
								uppCustomerStreet2: "",
								uppCustomerCity: this.form.city,
								uppCustomerCountry: this.form.countryCode,
								uppCustomerZipCode: this.form.postcode,
								uppCustomerPhone: this.form.phoneNumber,
								uppCustomerEmail: this.form.email,
								useAlias: true,
								uppRememberMe: "checked"
							}
						});
					}
					else {
						this.response.message = response.error;
						this.response.type = 'error';
						this.$nextTick(() => {
							this.$refs.message.$el.scrollIntoView();
						});
						this.loading = false;
					}
				}
				catch(error) {
					this.response.message = error.message;
					this.response.type = 'error';
					this.loading = false;
				}
			}
		},
		resetForm() {
			this.$refs.form && this.$refs.form.reset();
			this.form.firstname = '';
			this.form.lastname = '';
			this.form.company = '';
			this.form.street = '';
			this.form.postcode = '';
			this.form.city = '';
			this.form.phoneNumber = '';
			this.form.email = '';
			this.form.message = '';
			this.form.checkAgb = false;
			this.vouchers.forEach(voucher => {
				voucher.quantity = 0;

				if (voucher.custom) {
					voucher.price = 0;
				}
			});
		},
	},
	created() {
		this.loading = true;
	},
	mounted() {
		this.loading = false;
	}
}
</script>

<style>

</style>
